/**
 * The Features Guide: a user manual, one chapter per panel, written from the
 * app itself (app.meetnplan.co), with its labels as the app shows them.
 *
 * Each chapter has an `id` (its link, as in /guide#admin-panel), a `title`, an
 * `intro` and `who` uses it, then its `features`. A feature has an `id` (unique
 * on the page), a `title`, a `text`, `steps` and an optional `tip`. Numbers
 * (2.4) and the contents list follow the order here. Mirror every change in
 * `../ar/guide.js`.
 */
export const GUIDE = {
  eyebrow: 'User manual',
  title: 'Every feature, and how to use it',
  text: 'Step-by-step instructions for every panel. Read it from start to finish, or search for what you need.',
  search: {
    label: 'Search the manual',
    placeholder: 'Search a feature, e.g. invite, rooms, visitor',
  },
  contentsLabel: 'Contents',
  chapterLabel: 'Chapter',
  whoLabel: 'Who uses it:',
  stepsLabel: 'Steps',
  tipLabel: 'Good to know.',
  results: (count, query) =>
    `${count} ${count === 1 ? 'section matches' : 'sections match'} “${query}”`,
  clear: 'Clear search',
  none: 'Nothing in the manual matches your search.',
  help: {
    title: 'Still stuck?',
    text: 'Admins can send a request from Support in the Admin panel, or write to us.',
    cta: { label: 'Contact us', href: '/contact' },
  },
  chapters: [
    {
      id: 'getting-started',
      title: 'Getting started',
      intro: 'Everything you need for your first few minutes in MeetNPlan.',
      who: 'Everyone',
      features: [
        {
          id: 'sign-in',
          title: 'Sign in',
          text: 'Everyone signs in at app.meetnplan.co. Members open the booking app, and admins, HR and reception each open their own panel.',
          steps: [
            'Go to app.meetnplan.co, enter your email and press Next. You can also press Continue with Google.',
            'To open a panel, press the Member pill beside your email and pick Admin, HR or Reception.',
            'Enter your password. Leave Keep me signed in ticked only on your own computer.',
            'Press Enter dashboard, or Open Admin panel, Open HR panel or Open Reception panel.',
            'If MeetNPlan says your account signs in from another page, press the Switch to link it shows.',
          ],
          tip: 'Each sign-in page has its own address you can bookmark: app.meetnplan.co/admin/login, app.meetnplan.co/hr/login and app.meetnplan.co/reception/login.',
        },
        {
          id: 'create-organization',
          title: 'Create your organization',
          text: 'Set up a new workspace for your company in a few steps. You become its admin and can bring your team in straight away.',
          steps: [
            'Open app.meetnplan.co/get-started, or press create an organization on the sign-in page.',
            'Enter your First name, Last name, Work email and Password, then press Continue. Or press Continue with Google to skip the password.',
            'Type the Organization name. Keep Let teammates on @yourdomain ask to join ticked if colleagues should join with their work email. Tick I accept the Terms of Service for my company and I have read the Privacy Policy. Press Continue, or Create organization if you used Google.',
            'Enter the 6-digit code from your email and press Verify & create organization.',
            'Share the Company code or the Invite link, or add addresses under Email, then press Continue. Press Skip for now to invite people later from the admin panel.',
          ],
          tip: 'A personal email such as Gmail works too. Your team then joins with the company code or an invite link.',
        },
        {
          id: 'join-your-team',
          title: 'Join your team',
          text: 'Ask to join your company’s workspace with your work email or the company code from your admin. An admin or HR approves each request, and MeetNPlan emails you once you are in.',
          steps: [
            'On the sign-in page, press Sign up next to New to MeetNPlan?',
            'Choose Work email or Company code, fill it in, and press Continue once your workspace shows.',
            'Enter your First name, Last name and Password (and Email, if you used a code), tick I have read the Privacy Policy, and press Request to join.',
            'Enter the 6-digit code from your email and press Verify & create account.',
            'Press See request status to follow your request, and Check status to refresh it.',
          ],
          tip: 'Passwords need 8 or more characters with an uppercase letter, a lowercase letter, a number and a symbol. MeetNPlan also refuses a password found in a known data breach.',
        },
        {
          id: 'accept-invitation',
          title: 'Accept an invitation',
          text: 'An admin can email you an invitation or share an invite link. Either one opens a short form to join that company.',
          steps: [
            'In the invitation email, press the Join button with your company’s name, or open the invite link you were given.',
            'Enter your First name, Last name, Email and Password, tick I have read the Privacy Policy, then press Continue.',
            'Enter the 6-digit code from your email and press Verify & request access.',
            'Keep the Waiting for approval page open. Once you are approved it says You’re in and opens your workspace.',
          ],
          tip: 'An emailed invitation works only with the address it was sent to. If a link has expired, ask for a fresh one.',
        },
        {
          id: 'sign-up-with-google',
          title: 'Sign up with Google',
          text: 'New to MeetNPlan with a Google account? After you choose it, one last screen asks which workspace to join.',
          steps: [
            'Press Continue with Google on the sign-in page, or Sign up with Google on the sign-up page, and pick your Google account.',
            'On Complete your profile, check the workspace that matches your email, or enter the Company code from your admin.',
            'Tick I have read the Privacy Policy.',
            'Press Request to join, or Request access if you entered a code.',
          ],
          tip: 'Press Not you? to sign out and choose a different Google account.',
        },
        {
          id: 'reset-password',
          title: 'Reset a forgotten password',
          text: 'Set a new password with a 6-digit code sent to your email. Changing it signs you out on your other devices.',
          steps: [
            'On the sign-in page, enter your email, press Next, then press Forgot password?',
            'Check the address under Email and press Send code.',
            'Enter the 6-digit code and press Verify code.',
            'Type your New password and Confirm password, then press Update password.',
            'Press Log in and sign in with your new password.',
          ],
          tip: 'A code works for 10 minutes. You can ask for a new one each minute, up to five an hour.',
        },
        {
          id: 'find-your-way-around',
          title: 'Find your way around',
          text: 'The booking app and every panel share the same sidebar, so each one feels familiar.',
          steps: [
            'Use the sidebar on the left to open pages. Press a row such as My Meetings to show its views, like Live now or Upcoming.',
            'On a tablet or a narrow window, the sidebar becomes a slim bar of icons. Press your picture at the bottom to open the full menu.',
            'Press Admin Panel, HR Panel or Reception Panel in the booking app to reach your panel, and Open booking app to come back. Reception sees Open booking app only if the company allows it.',
            'Press your name at the bottom of the booking app sidebar to open your Profile.',
            'Press Ctrl K (⌘K on a Mac) in the booking app to search meetings, rooms and people.',
          ],
          tip: 'MeetNPlan is built for tablets and laptops, so a phone shows a notice instead of the app.',
        },
        {
          id: 'notification-cards',
          title: 'Notifications',
          text: 'New notifications slide in as cards at the top corner of the page, then tuck away after 7 seconds.',
          steps: [
            'Answer a request right on its card, or press × to dismiss it.',
            'Double-click (or double-tap) an empty part of any page header to open all your notifications.',
            'Press Show less to fold the list, or Hide to put it away. Press Clear all twice to clear everything.',
            'In the booking app, open Notifications in the sidebar for the full list, with All, Unread, Read and Needs action views.',
          ],
          tip: 'In the booking app, the same corner shows a card with the steps left to set up your account, such as Add your phone number or Choose your email notifications.',
        },
        {
          id: 'language-and-sign-out',
          title: 'Switch language and sign out',
          text: 'MeetNPlan works in English and Arabic. In Arabic the whole layout reads right to left.',
          steps: [
            'Press the ع button at the bottom of the sidebar, or at the top of the sign-in page, to switch to Arabic. In Arabic the same button shows EN.',
            'To keep a language on your account, open Profile, pick it under Language in Language & dates, and press Save settings.',
            'Press the Sign out button beside the language button at the bottom of the sidebar.',
          ],
          tip: 'Language & dates also lets you pick Gregorian or Hijri under Date system.',
        },
      ],
    },
    {
      id: 'user-panel',
      title: 'User panel',
      intro:
        'The User panel is where people book rooms, run meetings and stay in touch. Every member of your company has it.',
      who: 'Everyone in your company',
      features: [
        {
          id: 'dashboard',
          title: 'Your dashboard',
          text: 'The Dashboard shows your day at a glance: what is on now, what comes next, the invitations waiting for your reply and how busy the week is.',
          steps: [
            'Open Dashboard to see Up Next, or Happening now while a meeting is live.',
            'Press Join now or Join early for an online meeting, or View meeting to see the details.',
            'Press a tile, such as Awaiting my reply or Needs my decision, to go straight to that list.',
            'Next to Today, pick In the office, Remote or Out so reception knows where you are today.',
          ],
          tip: 'Press Ctrl+K (⌘K on a Mac) on any page to search meetings, rooms and people.',
        },
        {
          id: 'schedule-meeting',
          title: 'Schedule a meeting',
          text: 'One short form books the room, sets the time and sends the invitations. It only asks what your kind of meeting needs.',
          steps: [
            'Press Schedule Meeting in the sidebar.',
            'Choose how you will meet: Room, Online or Visit (an off-site place), then press Next.',
            'Enter the Meeting name and pick the Meeting type: Internal, Client or Personal.',
            'Set the Date, Start time and Duration, then pick the room, link or place.',
            'Add people on the Guests step, check the Review step and press Confirm & schedule.',
          ],
          tip: 'If your company asks HR to approve room bookings, the button reads Send request, and invitations go out once HR approves.',
        },
        {
          id: 'online-meetings',
          title: 'Online and hybrid meetings',
          text: 'Choose Online to meet by video. MeetNPlan can create a Google Meet link for you, or you can paste your own link.',
          steps: [
            'Press Schedule Meeting and choose Online.',
            'On the Link step, MeetNPlan creates a Google Meet link when you confirm. Without one, paste a Zoom, Teams or Meet link.',
            'For a hybrid meeting, tick Also book a room for people in the office and pick a room.',
            'When the meeting starts, press Join now on the Dashboard or Join in My Day.',
          ],
          tip: 'A Meet link is created only when your Google Calendar is connected in your Profile and your plan includes Meet links.',
        },
        {
          id: 'client-guests',
          title: 'Invite clients and outside guests',
          text: 'Invite people from outside your company by email, and reception expects them when the meeting is in a room. This depends on your plan, and HR can turn outside guests off.',
          steps: [
            'For a client meeting, pick Client as the Meeting type, then enter each client’s Full name, Email and Phone on the Client step.',
            'To add anyone else from outside, open the Guest tab on the Guests step, enter their details and press Add. A meeting in a room also needs their company and phone.',
            'Turn on VIP for a client coming to a room so they skip the visitor form at reception.',
            'Guests get an email with the time and place, and reply with Yes, I’ll attend, Decline or Request a different time.',
            'When reception signs your guest in, you get a Visitor Arrived notification.',
          ],
          tip: 'Guests also get a reminder email before the meeting. Your admin sets how early it goes out.',
        },
        {
          id: 'recurring-meetings',
          title: 'Repeat a meeting',
          text: 'Book the same meeting on several dates in one go. Repeating meetings depend on your plan.',
          steps: [
            'On the When step, turn on Repeat this meeting.',
            'Under Repeats, choose Daily, Weekly, Monthly or Custom dates. For Weekly, pick the days under Repeat on; for Custom dates, tap the dates you want.',
            'Under Ends, choose Never, After a number of times, or On a date.',
            'On the Dates step, check each date, change the time of any that clash or remove them, then press Confirm & schedule.',
          ],
          tip: 'Dates are booked only as far ahead as your company allows, and off days are skipped.',
        },
        {
          id: 'conflicts',
          title: 'Busy times and suggested times',
          text: 'Before you book, MeetNPlan checks the room, your own calendar and your colleagues’ calendars, and tells you who is busy and by how many minutes.',
          steps: [
            'On the Room step, only rooms free at that time are listed, unless it is a client meeting.',
            'If you already have a meeting then, Review shows You’re already booked then with the overlap in minutes.',
            'Under Pick a new time, press one of the suggested times, or press Choose another time.',
            'If people you invited are busy, MeetNPlan names them. You can still book, and they can decline.',
          ],
          tip: 'Busy times in a colleague’s connected Google Calendar count too. Guests outside your company can’t be checked, and MeetNPlan tells you so.',
        },
        {
          id: 'answer-invites',
          title: 'Answer invitations',
          text: 'Every meeting you are invited to is in one list, and you reply with one press. The organizer is told your answer.',
          steps: [
            'Open Invited Meetings and choose All, or Awaiting for the ones you have not answered yet.',
            'Press Accept, Maybe or Decline on the invitation.',
            'When you decline, add a reason if you like and press Decline again to confirm.',
            'To decline several at once, press Select, pick the invitations and press Decline selected.',
          ],
          tip: 'You can also press Accept or Decline straight from the invitation email, and change your answer later in the app.',
        },
        {
          id: 'meeting-details',
          title: 'Open a meeting',
          text: 'Every meeting opens in one window with its time, place, link and attendees. Meeting links in MeetNPlan emails open the same window.',
          steps: [
            'Press a meeting in the Calendar, press View meeting on the Dashboard, or open a meeting link from an email.',
            'See the room, link or address, the attendees and how many of them have accepted.',
            'Press the meeting link to join, or press Open chat to message the attendees.',
            'If you are the organizer, press Delete to cancel it for everyone.',
          ],
          tip: 'If the meeting was cancelled after the email was sent, the link shows This meeting isn’t available.',
        },
        {
          id: 'my-day',
          title: 'My Day',
          text: 'My Day lists your own meetings for one day, past, live and upcoming, whether they are in a room, online or a visit.',
          steps: [
            'Open My Day. Use the arrows to move to another day, and press the day in the middle to come back to today.',
            'On a live meeting, press Join to open its video link.',
            'As the organizer, press + 15m to keep the room 15 more minutes, or End to finish now and free the room.',
            'If your company releases unconfirmed rooms, press We’re here to confirm the room is in use.',
          ],
          tip: 'You can extend a meeting by up to one hour in total. After that, reschedule it instead.',
        },
        {
          id: 'calendar',
          title: 'Calendar',
          text: 'See your company’s meetings by day, week or month, or side by side by room, and book a free slot straight from the grid.',
          steps: [
            'Open Calendar and choose Day, Week, Month or Rooms.',
            'Use the arrows to move through time, press the period title to come back to today, and press Filter rooms to show only the rooms you need.',
            'Under Calendar in the sidebar, pick My meetings, Past, Live now or Upcoming to narrow the view.',
            'In Day, Week or Rooms view, drag across empty time to book it. The form opens with the time already filled in, and the room too in Rooms view.',
          ],
        },
        {
          id: 'drag-calendar',
          title: 'Drag to move or extend',
          text: 'Move your own meeting by dragging it to a new time, or change its length by dragging its bottom edge. Everyone invited is told about the change.',
          steps: [
            'Open Calendar in Day or Week view.',
            'Drag one of your meetings to a new time or day.',
            'Drag its bottom edge down to make it longer, or up to make it shorter.',
            'A short message, such as Moved to or Duration set to, confirms the change.',
          ],
          tip: 'If the room or you are busy at the new time, the meeting stays where it was. A meeting in progress can only be given more time.',
        },
        {
          id: 'manage-meetings',
          title: 'Reschedule or cancel a meeting',
          text: 'My Meetings lists the meetings you organise, so you can move them, cancel them or clear out old ones. Invitees are told about every change.',
          steps: [
            'Open My Meetings and choose All, or a view such as Upcoming, Cancelled, Room, Online or Visit.',
            'Point to a meeting, press Reschedule meeting, pick the new Date, Time, Duration or Room and press Reschedule.',
            'To cancel, press Delete meeting and confirm.',
            'When you delete a repeating meeting, choose This meeting only, This and following or All in the series.',
          ],
          tip: 'Any room a deleted meeting held frees up straight away for other bookings.',
        },
        {
          id: 'action-required',
          title: 'Action Required',
          text: 'Requests that need your decision wait here, such as a client meeting asking for a slot you booked, or a guest asking to move a meeting. When your own client meeting needs a busy slot, Review offers Submit priority request.',
          steps: [
            'Open Action Required and choose Needs your decision.',
            'For a client meeting that wants your slot, press Reject request, Reschedule mine or Cancel mine.',
            'Reschedule mine suggests Same time, another room and Same room, another time, so you can keep your meeting.',
            'For a guest who asked to move a meeting, press Approve on a time they proposed, Suggest another time, or Keep the current time.',
            'Open Requests you sent to follow your own client requests, and press Withdraw request if you no longer need the slot.',
          ],
          tip: 'A priority request that is not answered within 20 minutes is declined automatically, and the booked meeting keeps its slot.',
        },
        {
          id: 'rooms',
          title: 'Find a free room',
          text: 'Meeting Rooms shows every room with its seats and amenities, whether it is in use now, and when it is free next.',
          steps: [
            'Open Meeting Rooms and choose All, Free or Occupied.',
            'Search by name, or set the room size to find a room big enough for your group.',
            'Press Book Room on a free room. The form opens with that room already picked.',
            'On a room in use, press Notify me when free to hear if it frees up.',
          ],
          tip: 'Notify me when free is a notice, not a hold. The room goes to whoever books it first.',
        },
        {
          id: 'messages',
          title: 'Messages',
          text: 'Chat with colleagues one to one or in groups, inside your company workspace. Messages depends on your plan, and your company’s settings decide who can create groups and share files.',
          steps: [
            'Open Messages and press New message.',
            'Pick a colleague, a team under Teams such as Reception or HR, or press Create a group.',
            'Type your message and press Send. Seen appears once it has been read.',
            'Press + to add a Photo, File, Voice note, or a Poll in a group. With a message typed, choose Schedule message to send it later.',
            'Point to a message and press Reply, or More actions to edit, forward or pin it, or see Message info.',
          ],
          tip: 'In a group, type @ and a name to mention someone. They get a notification.',
        },
        {
          id: 'meeting-chats',
          title: 'Meeting chats',
          text: 'A confirmed meeting with at least one other colleague gets its own chat for everyone attending. Time and room changes, and guests arriving at reception, are posted there.',
          steps: [
            'Open Messages and find the chat under Meeting chats, or press Open chat on the meeting.',
            'Write a comment for the attendees, share a file or start a poll.',
            'Press View booking to see the meeting itself.',
          ],
          tip: 'The chat stays open for a day of follow-ups after the meeting ends. Then the organizer chooses Keep or Delete.',
        },
        {
          id: 'notifications',
          title: 'Notifications',
          text: 'Invitations, replies, reminders, changes and visitor arrivals all land in one list, so nothing gets missed.',
          steps: [
            'Open Notifications and choose All, Unread, Read or Needs action.',
            'Press a notification to see its details, or press its button, such as Respond or Confirm Room.',
            'Press Mark all read, or press Select to mark or delete several at once.',
          ],
          tip: 'Choose which notifications also arrive by email in your Profile, under Email me about.',
        },
        {
          id: 'history',
          title: 'Meeting history',
          text: 'Meeting History keeps a record of every change to your meetings: created, rescheduled, extended, ended early or removed.',
          steps: [
            'Open Meeting History and choose All, Created, Rescheduled or Removed.',
            'Use the search box to find a meeting by name.',
            'Press a record to see what changed, such as the old time and the new one.',
            'Press Select to delete records you no longer need.',
          ],
          tip: 'Deleting a record only removes it from your history. The meeting itself does not change.',
        },
        {
          id: 'google-calendar',
          title: 'Google Calendar',
          text: 'Connect Google Calendar and every meeting you schedule is added to it, with a Meet link and invites for your guests. Reschedules and cancellations sync too.',
          steps: [
            'Press your name at the bottom of the sidebar to open your Profile.',
            'On the Google Calendar card, press Connect and sign in with your Google account.',
            'Press the refresh button, Sync existing meetings, to add meetings booked before you connected.',
          ],
          tip: 'Calendar sync depends on your plan. Once connected, your Google Calendar busy times also count when colleagues check if you are free.',
        },
        {
          id: 'profile-language',
          title: 'Profile, email alerts and language',
          text: 'Keep your phone number up to date, choose which emails you get, and use MeetNPlan in English or Arabic with Gregorian or Hijri dates.',
          steps: [
            'Press your name at the bottom of the sidebar to open your Profile.',
            'Add your Mobile number so reception can call you when a visitor arrives.',
            'Under Email me about, switch off any emails you don’t need.',
            'Under Language & dates, pick English or العربية, and Gregorian or Hijri.',
            'Press Save settings.',
          ],
          tip: 'To switch language in one press, use the ع button next to your name in the sidebar. Your choice is saved to your account.',
        },
      ],
    },
    {
      id: 'reception-panel',
      title: 'Reception panel',
      intro:
        'The Reception panel is for the front desk. It signs visitors in and out and lets hosts know when their guests arrive.',
      who: 'Reception staff',
      features: [
        {
          id: 'front-desk',
          title: 'Read the front desk at a glance',
          text: 'The Front desk is the first screen of the day. It shows today’s date, how long the workspace stays open and today’s numbers, so you know how busy the building is before the first guest arrives.',
          steps: [
            'Open Front desk at the top of the sidebar.',
            'Read the four big numbers: Rooms free, Meetings today, On site and In the office.',
            'Check More today for who is In a meeting, Still to arrive or Running late.',
            'Press a number such as On site or In the office to open the list behind it.',
          ],
          tip: 'The Live pill on the clock card means the screen is up to date, and Reconnecting means it is catching up.',
        },
        {
          id: 'needs-attention',
          title: 'Act on what needs attention',
          text: 'Needs attention lists anyone who could be kept waiting, most urgent first: a late guest, a meeting whose guests are not here yet, a visitor whose host is out, a room about to be released, or a visitor still in after closing. Each item comes with one action.',
          steps: [
            'On the Front desk, read the Needs attention card. All clear means nobody needs a nudge.',
            'Press the action on an item, such as Sign in guest, Open meeting, Call host, Remind host or Mark as left.',
            'Open an item to see its details, and press Call to phone the person it is about.',
            'Press Silence to hide an item for 15 minutes. It comes back if it still needs you.',
          ],
          tip: 'On every other tab, the same alerts appear as floating cards, so you do not miss one while you work.',
        },
        {
          id: 'expected-guest',
          title: 'Sign in an expected guest',
          text: 'Guests invited to today’s and tomorrow’s meetings are listed under Visitors › Upcoming, with their details filled in from the invitation. Upcoming is there when your plan includes guest invitations.',
          steps: [
            'Open Visitors, then Upcoming, and choose Today or Tomorrow. Pick Still to arrive or Late to narrow the list.',
            'Press Sign in on the guest’s row. Check the Guest details, and press Edit to correct anything that has changed.',
            'Press Next. Under Anyone with them?, choose Yes, someone is with them and add each person, or No, they are on their own.',
            'Check Confirm the visit and press Next, then hand the screen to the guest to sign in the box.',
            'Press Sign in. The host is notified, and the sheet shows the signature on file until you press Done.',
          ],
          tip: 'If your admin has turned off arrival signatures, you press Sign in on Confirm the visit and the guest signs nothing.',
        },
        {
          id: 'walk-in',
          title: 'Sign in a walk-in visitor',
          text: 'A walk-in is someone who arrives without an invitation, such as a courier or an interview candidate. Sign in walk-in sits in the sidebar, so it is one press away from every tab.',
          steps: [
            'Press Sign in walk-in in the sidebar, or on the Visitors page.',
            'Enter the Full name and Company, then the mobile number under Phone. Press Continue after each screen.',
            'Pick the host they are meeting, then choose the purpose of the visit, such as Meeting or Delivery.',
            'Use Extra people to add anyone with them. If your workspace asks for a signature, the visitor signs next.',
            'Check the Review screen and press Sign in.',
          ],
          tip: 'The host is notified as soon as the visitor signs in. If nobody could be told, the last screen says so, so call the host.',
        },
        {
          id: 'vip',
          title: 'Welcome a VIP guest',
          text: 'When a host marks a guest as VIP while booking a client meeting in a room, the guest is listed under VIP guests on the day. VIP guests sign nothing: one press signs them in, with no form.',
          steps: [
            'Open VIP guests, or search for the guest or their host.',
            'Press Sign in when the guest arrives. The host is notified.',
            'When the guest leaves, press Mark as left.',
            'If they come back while their meeting is still on, press Sign in again.',
          ],
          tip: 'VIP guests also appear in Visitors with a VIP tag, and signing them in there skips the form too.',
        },
        {
          id: 'host-alerts',
          title: 'Follow host alerts and replies',
          text: 'Each time you sign a visitor in, MeetNPlan tells the host in their Reception chat, and the host can answer with one press. Host alerts shows every alert, whether the host saw it and what they replied, when Messages is on for your workspace.',
          steps: [
            'Open Host alerts. The top shows today’s Sent, Replied and Still waiting.',
            'Choose Today, Yesterday or This week, or search for a visitor or host.',
            'Open a row’s details to see when the alert was sent, when the host saw it, and their Reply, such as On my way or Wait 5 min.',
            'If the visitor is still waiting, press Send again. Press Open chat to write to the host yourself.',
          ],
          tip: 'To message a host directly, press Notify a host and pick them, and their chat opens in Messages.',
        },
        {
          id: 'whos-in',
          title: 'Find a colleague in Who’s in',
          text: 'Who’s in shows where every colleague is today, in the office, in a meeting or out on a visit, with the number to reach them on. Use it when a visitor asks for someone.',
          steps: [
            'Open Who’s in, or pick a view under it in the sidebar, such as In the office or Free.',
            'Search by name or email, then open the person’s row.',
            'Read Now and Next for their current and next meeting, and press Call or Email to reach them.',
            'Press Mark in when they arrive and Mark out when they leave.',
          ],
          tip: 'Free means no meeting is booked in MeetNPlan, so they may still be busy with something else.',
        },
        {
          id: 'visitor-record',
          title: 'Check and correct a visit',
          text: 'Every visit has a record with the company, host, meeting, the people with the visitor, both signatures and a timeline of what happened. Open it from any row in Visitors.',
          steps: [
            'Open Visitors and press a visitor’s row to open their record.',
            'Press Edit visit to correct the visitor’s details or host, then press Save changes.',
            'Under People, press Add person to add someone who joined them later.',
            'While the visitor waits, press Call host, or Remind host to tell the host they are still waiting.',
          ],
          tip: 'You can remind a host once every 5 minutes for the same visitor.',
        },
        {
          id: 'reception-messages',
          title: 'Answer colleagues in Messages',
          text: 'Colleagues can write to Reception, and whoever is at the desk sees it, when Messages is on for your workspace. Chats are sorted by who is answering them: Waiting, Mine, Others and Earlier.',
          steps: [
            'Open Messages and pick a chat under Waiting.',
            'Press Answer so the rest of the desk knows you have it, then reply.',
            'Press Pass to… to hand the chat to a colleague, or Done to give it back to the team.',
            'Press New message to write to anyone in the company as Reception.',
          ],
          tip: 'If the colleague answering a chat has been away for 7 minutes, you can press Take over and answer it yourself.',
        },
        {
          id: 'sign-out-visitor',
          title: 'Sign a visitor out',
          text: 'Sign visitors out when they leave, so the visitor log shows who is still inside. Everyone who came with them is signed out at the same time.',
          steps: [
            'Open Visitors, then On site.',
            'Press Sign out on the visitor’s row.',
            'Check who is leaving, ask the visitor to sign in the box, then press Sign out.',
            'If they have already gone without signing, press Visitor already left instead.',
          ],
          tip: 'If the visitor cannot draw a signature, press Type instead and type their name.',
        },
      ],
    },
    {
      id: 'hr-panel',
      title: 'HR panel',
      intro:
        'The HR panel is where requests are approved and people are looked after.',
      who: 'Your HR team',
      features: [
        {
          id: 'hr-needs-action',
          title: 'Start from Needs your action',
          text: 'The HR dashboard gathers what is waiting on HR: people with no phone number, join requests, unanswered invites, booking requests and booking clashes.',
          steps: [
            'Open Dashboard and look at Needs your action.',
            'Press Review on Join requests or Booking requests to decide them.',
            'Press View invites to see email invites not accepted yet.',
            'Press Review clashes to see client requests that overlap a meeting.',
          ],
          tip: 'Booking requests shows while Book rooms directly is off or requests are waiting, and Booking clashes only on plans with client meetings.',
        },
        {
          id: 'phone-reminders',
          title: 'Remind people to add a phone number',
          text: 'Reception can’t call people who have no phone number when their guests arrive. The Missing phone number card lists them so you can remind them by email.',
          steps: [
            'Open Dashboard and find Missing phone number.',
            'Press Remind next to a person.',
            'Or press Remind all to remind everyone on the list at once.',
          ],
          tip: 'People leave the list as soon as they add a number.',
        },
        {
          id: 'hr-shared-screens',
          title: 'Work with people, rooms and bookings',
          text: 'HR uses the same Users, Invite members, Rooms, Bookings and Client Bookings screens as admins. HR doesn’t change roles and has no Billing or Support.',
          steps: [
            'Open Users › All to remove a member or reopen a declined request. Only an admin can remove an admin.',
            'Open Invite members › Overview to send email invites and copy the shareable link, which an admin turns on or off.',
            'Open Invite members › Pending requests to approve or decline people who asked to join.',
            'Open Rooms › Add or edit room to add, edit or delete rooms, just as admins do.',
            'Open Bookings › All to approve or decline waiting bookings. Deleting bookings and overriding client clashes stay with admins.',
          ],
          tip: 'Everyone you invite joins as a User. An admin can change roles later.',
        },
        {
          id: 'activity-log',
          title: 'Follow the activity log',
          text: 'Activity lists joins, invites, role changes, room changes, phone reminders, booking approvals and clashes across the company, newest first and grouped by day.',
          steps: [
            'Open Activity › All activity.',
            'Pick People, Invites, Rooms or Bookings to see one area at a time.',
            'Search for a person, action or detail.',
            'Press a row to see who did it, when, and its Event ID.',
          ],
        },
        {
          id: 'hr-settings',
          title: 'Manage settings as HR',
          text: 'HR’s Settings page has the same member permissions, booking rules, working hours, days off, unattended rooms and visitor settings as the admin’s. The name, logo and domains stay with your admin.',
          steps: [
            'Open Settings.',
            'Change what you need under Member permissions, Booking rules, Working hours, Days off, Unattended rooms, Visitors or Visitor arrival.',
            'Press Save settings, or Discard to undo your changes.',
          ],
          tip: 'Your company code is shown at the top of the page, ready to share with new people.',
        },
        {
          id: 'hr-emails',
          title: 'Choose HR emails',
          text: 'MeetNPlan can email the HR team about new requests, cancellations and a weekly summary. All three are off until you switch them on.',
          steps: [
            'Open Settings › Notifications.',
            'Switch on New requests, Cancellations or Weekly summary.',
            'Press Save settings.',
          ],
          tip: 'The weekly summary covers bookings and visitors and arrives every Sunday morning.',
        },
        {
          id: 'send-announcements',
          title: 'Send announcements',
          text: 'HR and admins can send announcements in Messages to everyone, chosen roles or specific people, unless an admin limits this to admins only. Messages shows only when it is available for your workspace.',
          steps: [
            'Open Messages and press New announcement.',
            'Under Send to, pick Everyone, Roles or Specific people.',
            'Write the Title and Message, and switch on Urgent, Pin to top or Confirm read if you need them.',
            'Press the send button, which shows how many people it goes to.',
          ],
          tip: 'Urgent announcements are shown in red and emailed too.',
        },
      ],
    },
    {
      id: 'admin-panel',
      title: 'Admin panel',
      intro:
        'The Admin panel sets up your company: rooms, people, rules and billing.',
      who: 'Company admins',
      features: [
        {
          id: 'admin-dashboard',
          title: 'Read your dashboard',
          text: 'The dashboard shows what needs your attention, how busy the workspace is and how much of your plan you use.',
          steps: [
            'Open Dashboard.',
            'Check the attention list at the top, then press Open requests or Review now to act on what is waiting.',
            'Read Bookings this month, Rooms free now and Usage this period to see how the workspace is used.',
            'Press Manage plan to go straight to Billing.',
          ],
          tip: 'Plan warnings, such as an overdue payment or going over a limit, also appear in the attention list.',
        },
        {
          id: 'set-up-rooms',
          title: 'Set up rooms',
          text: 'Add each meeting room with its seats, equipment and a photo so your team can book it. How many rooms you can add depends on your plan.',
          steps: [
            'Open Rooms › Add or edit room, or press Add Room.',
            'Choose New room, or pick a room under Or edit an existing room, then press Next.',
            'Enter the Room name and Capacity, then press Next.',
            'On the Equipment step, tick everything in the room and add a Room photo if you have one (JPG, PNG or WEBP, up to 8 MB).',
            'Press Create room, or Save changes for an existing room.',
          ],
          tip: 'The preview beside the editor updates as you type, and nobody else sees the room until you save.',
        },
        {
          id: 'company-identity',
          title: 'Set your company identity',
          text: 'Give the workspace your organization’s name, logo, language and calendar, and list the email domains your team joins with.',
          steps: [
            'Open Settings › Identity.',
            'Press Upload logo, and press Edit name to change the Organization name.',
            'Under Language & calendar, choose the workspace language and Gregorian or Hijri.',
            'Under Authorized domains, type your email domain, such as company.com, and press Add.',
            'Press Save settings.',
          ],
          tip: 'Personal email providers can’t be a company domain, and a domain already claimed by another organization can’t be added.',
        },
        {
          id: 'invite-your-team',
          title: 'Invite your team',
          text: 'Bring people in with a shareable link, an email invite or your permanent company code. Every request to join still needs approval.',
          steps: [
            'Open Invite members › Overview.',
            'Switch on Shareable invite link and press Copy to share it.',
            'Under Invite by email, type up to 10 addresses separated by a comma or space, then press Send.',
            'To use the company code instead, open Settings › Identity and press Copy next to Company code.',
          ],
          tip: 'Press Regenerate link to replace the link. The old one stops working.',
        },
        {
          id: 'approve-join-requests',
          title: 'Approve join requests',
          text: 'Everyone who joins by link, email, company code or company domain waits for approval. You decide who gets in, and each person is told the result.',
          steps: [
            'Open Invite members › Pending requests.',
            'Press View details to see when and how the person asked to join.',
            'Press Approve or Decline, or press Approve all to let everyone in.',
            'To give a declined person another chance, open Users › Declined, press View details on their row and press Reopen request.',
          ],
          tip: 'Pending requests hold a seat until you decide, so approving stops when your plan has no seats left.',
        },
        {
          id: 'roles-and-access',
          title: 'Change roles and remove people',
          text: 'Give each person the role they need: User books rooms and meetings, HR sees member details, Reception handles guest arrivals and Admin has full workspace access. Some roles are limited by your plan.',
          steps: [
            'Open Users › All and find the person.',
            'Pick User, HR, Reception or Admin from the role menu on their row.',
            'To remove someone, press the bin icon, type their name and press Delete. Their login, meetings and bookings are removed.',
          ],
          tip: 'You can’t change your own role or the founder’s, and the company always keeps at least one admin.',
        },
        {
          id: 'member-permissions',
          title: 'Decide what members can do',
          text: 'Member permissions set what members can do without HR approval, from booking rooms directly to inviting colleagues and guests.',
          steps: [
            'Open Settings › Member permissions.',
            'Switch Book rooms directly, Invite new members, Invite external guests and Let reception use the booking app on or off.',
            'Press Save settings.',
          ],
          tip: 'When Book rooms directly is off, members’ room bookings wait for approval in Bookings.',
        },
        {
          id: 'booking-rules',
          title: 'Set booking rules',
          text: 'Booking rules set limits for every booking in the workspace, and Unattended rooms frees up rooms nobody shows up for.',
          steps: [
            'Open Settings › Booking rules.',
            'Set Max booking length, Book up to and Allowed overlap.',
            'Under Unattended rooms, switch on Release rooms nobody confirms and set Wait before releasing.',
            'Switch on Cancel the meeting too if the meeting should also leave calendars.',
            'Press Save settings.',
          ],
          tip: 'With Release rooms nobody confirms on, everyone invited is asked to confirm, and the room opens for others if nobody does.',
        },
        {
          id: 'working-hours-days-off',
          title: 'Set working hours and days off',
          text: 'Working hours decide when rooms can be booked, and days off are hidden from the calendar and the booking form.',
          steps: [
            'Open Settings › Working hours.',
            'Pick the Start hour, End hour and Time zone.',
            'Under Days off, press a day to switch it between Open and Off.',
            'Press Save settings.',
          ],
          tip: 'Days off repeat every week, so pick the weekdays your office is closed.',
        },
        {
          id: 'welcome-visitors',
          title: 'Prepare for visitors',
          text: 'Tell guests how to find you. Your address, map, contact phone and instructions appear in their reminder and thank-you emails.',
          steps: [
            'Open Settings › Visitor arrival.',
            'Fill in Address, Map link (or press Use my location), Contact phone and Arrival instructions, and add a Feedback link if you have one.',
            'Under Visitor guide (PDF), press Add a PDF to attach a parking map or floor plan of up to 10 MB.',
            'Under Visitors, set Send arrival details, Keep visitor records and Ask for a signature on arrival.',
            'Press Save settings.',
          ],
          tip: 'With Ask for a signature on arrival on, guests sign on the reception tablet when they check in.',
        },
        {
          id: 'manage-bookings',
          title: 'Oversee bookings',
          text: 'Bookings lists every meeting booked across your organization: room bookings, client visits and online meetings.',
          steps: [
            'Open Bookings › All, or pick Client, Internal or Personal.',
            'Search, then press View details on a row to see its room, time, organizer and attendees.',
            'For a booking marked Pending, press Approve, or write a reason if you like and press Decline. The organizer is told either way.',
            'To remove bookings, press Select, tick them and press Delete selected.',
          ],
          tip: 'A booking waiting for approval holds the room until you decide, and expires when it starts.',
        },
        {
          id: 'client-bookings',
          title: 'Track client bookings',
          text: 'Client Bookings collects meetings with outside clients and their approval status. It shows on plans that include client meetings.',
          steps: [
            'Open Client Bookings › All, or open By status and pick Pending, Approved, Rescheduled, Rejected or Cancelled.',
            'Pick a room from the All rooms menu to narrow the list.',
            'When a client request clashes with a booked meeting, its organizer gets a set time to respond. Once Override ready shows, press Override to give the slot to the client.',
            'Open Client Bookings › Approval History to see every approval, rejection and override.',
          ],
          tip: 'Deleting a client meeting removes it for everyone invited, and guests with an email address are told by email.',
        },
        {
          id: 'chat-rules',
          title: 'Set rules for Messages',
          text: 'Decide who can start groups and send announcements, what people can share in chats and how long messages are kept. These cards appear in Settings once Messages is available for your workspace.',
          steps: [
            'Open Settings › Permissions.',
            'Under Create groups and Send announcements, choose who may do each.',
            'Under Content, switch File attachments and Voice notes on or off, and set Max file size and Edit and delete.',
            'Under Retention, set Keep messages and Meeting chats.',
            'Switch on Quiet hours under Announcements if needed, then press Save settings.',
          ],
          tip: 'With Quiet hours on, no urgent emails go out outside working hours or on days off.',
        },
        {
          id: 'manage-billing',
          title: 'Manage your plan and invoices',
          text: 'Billing shows your plan, what you use against its limits, and every payment you have made. A new plan’s limits apply once payment goes through.',
          steps: [
            'Open Billing › Current plan & usage to see your plan, renewal and usage.',
            'Open Billing › Plans & add-ons, then press Select plan on a plan, or use the add-on counters to add capacity.',
            'Check the Checkout summary and press Continue to payment.',
            'Open Billing › Invoices and press Download PDF on an invoice, or Download all.',
            'To stop, press Cancel plan on Current plan & usage, choose At the end of my billing period or Immediately, then press Cancel subscription.',
          ],
          tip: 'If your plan lapses, the panel turns read-only until you renew. Your data is safe, and Billing and Settings keep working.',
        },
        {
          id: 'get-support',
          title: 'Ask support for help',
          text: 'Send the MeetNPlan team a request from the panel and follow it until it is closed. Support is open on every plan, and Enterprise adds a named account manager.',
          steps: [
            'Open Support › Get help.',
            'Choose a topic under What is it about?, then fill in Subject and Details.',
            'Press Send request.',
            'Open Support › Your requests to see whether each request is Open, Answered or Closed.',
          ],
          tip: 'Replies arrive by email. Reply to that email to add anything, with no need to send another request.',
        },
        {
          id: 'danger-zone',
          title: 'Clear old data or delete the organization',
          text: 'The Danger zone at the bottom of Settings clears old bookings and meeting history, or deletes the whole organization. Members, rooms, settings and visitor records stay when you clear data.',
          steps: [
            'Open Settings › Danger zone.',
            'To clear old data, pick a date in Clear up to, press Clear data, type your organization’s name and press Yes, delete.',
            'To delete the organization, first remove the other members and cancel your subscription.',
            'Press Delete organization, type the organization’s name and press Delete organization again.',
          ],
          tip: 'A deleted organization can be brought back by signing in and pressing Restore within 30 days.',
        },
      ],
    },
  ],
}
